import { useSpecifications } from '@/hooks/useSpecifications';
import { SpecificationTemplate } from '@/lib/types/job';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DeleteSpecificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  specification: SpecificationTemplate | null;
}

export const DeleteSpecificationDialog = ({
  open,
  onOpenChange,
  specification,
}: DeleteSpecificationDialogProps) => {
  const { deactivateSpecification } = useSpecifications();

  const handleDelete = async () => {
    if (!specification) return;

    try {
      await deactivateSpecification(specification.id);
      onOpenChange(false);
    } catch (error) {
      // Error handled by hook
    }
  };

  const usageCount = specification?.usage_count || 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Specification</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              Are you sure you want to delete "{specification?.specification_name}"?
            </p>
            {usageCount > 0 && (
              <p className="font-semibold text-foreground">
                This specification is used in {usageCount} job{usageCount !== 1 ? 's' : ''}.
              </p>
            )}
            <p>
              This action will soft delete the specification. It won't appear in your library, but
              historical data for jobs using this specification will be preserved.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
